import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { adjustmentService } from "~/services/wms/adjustmentService";
import type {
  AdjustmentFilters,
  BulkAdjustmentItem,
  StockAdjustmentFormData,
} from "~/types/stockAdjustment";

// Query keys
const ADJUSTMENTS_KEY = "wms_adjustments";
const ADJUSTMENT_REASON_CODES_KEY = "wms_adjustment_reason_codes";
const ADJUSTMENT_STATS_KEY = "wms_adjustment_stats";

// ============================================================================
// Queries
// ============================================================================

/**
 * Get all active reason codes, optionally filtered by adjustment type
 */
export function useAdjustmentReasonCodes(
  adjustmentType?: "increment" | "decrement"
) {
  return useQuery({
    queryKey: [ADJUSTMENT_REASON_CODES_KEY, adjustmentType],
    queryFn: () => adjustmentService.getReasonCodes(adjustmentType),
  });
}

/**
 * Get list of adjustments with optional filters
 */
export function useAdjustments(filters?: AdjustmentFilters) {
  return useQuery({
    queryKey: [ADJUSTMENTS_KEY, filters],
    queryFn: () => adjustmentService.getAdjustments(filters),
  });
}

/**
 * Get a single adjustment by ID
 */
export function useAdjustment(id: string | undefined) {
  return useQuery({
    queryKey: [ADJUSTMENTS_KEY, id],
    queryFn: () => adjustmentService.getAdjustmentById(id!),
    enabled: !!id,
  });
}

/**
 * Get adjustment history for a specific product
 */
export function useProductAdjustmentHistory(productId: string | undefined) {
  return useQuery({
    queryKey: [ADJUSTMENTS_KEY, "product", productId],
    queryFn: () => adjustmentService.getAdjustmentsByProduct(productId!),
    enabled: !!productId,
  });
}

/**
 * Get adjustment statistics for the last N days
 */
export function useAdjustmentStats(days = 30) {
  return useQuery({
    queryKey: [ADJUSTMENT_STATS_KEY, days],
    queryFn: () => adjustmentService.getAdjustmentStats(days),
  });
}

// ============================================================================
// Mutations
// ============================================================================

// Every stock surface an adjustment (single or bulk) can move — invalidated after
// both mutations so the lists, KPIs and stock caches refresh.
function invalidateAdjustmentQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: [ADJUSTMENTS_KEY] });
  queryClient.invalidateQueries({ queryKey: [ADJUSTMENT_STATS_KEY] });
  queryClient.invalidateQueries({ queryKey: ["inventory-adjustments"] });
  // Product stock surfaces (WMS inventory + scalar cache)
  queryClient.invalidateQueries({ queryKey: ["wms_inventory"] });
  queryClient.invalidateQueries({ queryKey: ["wms_product_inventory"] });
  queryClient.invalidateQueries({ queryKey: ["products"] });
  // Ingredient stock surfaces — caches + per-plant source of truth
  queryClient.invalidateQueries({ queryKey: ["raw_materials"] });
  queryClient.invalidateQueries({ queryKey: ["oil_types"] });
  queryClient.invalidateQueries({ queryKey: ["raw_material_stock_by_plant"] });
  queryClient.invalidateQueries({ queryKey: ["oil_type_stock_by_plant"] });
  // Consulta de Stock read paths
  queryClient.invalidateQueries({ queryKey: ["unified-stock-search"] });
  queryClient.invalidateQueries({ queryKey: ["stock"] });
}

/**
 * Create a new stock adjustment
 */
export function useCreateAdjustment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      performedBy,
    }: {
      data: StockAdjustmentFormData;
      performedBy: string;
    }) => adjustmentService.createAdjustment(data, performedBy),
    onSuccess: () => invalidateAdjustmentQueries(queryClient),
  });
}

/**
 * Apply many adjustments from an uploaded file (Ajuste Masivo). Returns a per-row
 * result array (ok / error) rather than throwing on the first failure.
 */
export function useCreateBulkAdjustments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      items,
      performedBy,
    }: {
      items: BulkAdjustmentItem[];
      performedBy: string;
    }) => adjustmentService.createAdjustmentsBulk(items, performedBy),
    onSuccess: () => invalidateAdjustmentQueries(queryClient),
  });
}
